import React from "react";
import { Button, CircularProgress } from "@material-ui/core";

interface Props {
  show: boolean;
  loading: boolean;
  payload: unknown | null;
  variant: "success" | "error" | null;
  onDownload: () => void;
}

const formatKey = (k: string) =>
  k.replace(/([A-Z])/g, " $1").replace(/_/g, " ").replace(/^\s/, "").toLowerCase();

const formatValue = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

interface CalcExplanationProps {
  expl: Record<string, unknown>;
  colCount: number;
}

function CalcExplanation({ expl, colCount }: CalcExplanationProps) {
  const formulaKeys = Object.keys(expl).filter((k) => k.startsWith("formula"));
  const breakdownKeys = Object.keys(expl).filter((k) => !k.startsWith("formula"));
  return (
    <tr>
      <td
        colSpan={colCount}
        style={{ padding: "0 0 0 24px", background: "#f9f9f9", borderBottom: "2px solid #e0e0e0" }}
      >
        <div style={{ padding: "10px 12px 10px 0", display: "flex", flexDirection: "column", gap: 10 }}>
          {formulaKeys.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#666", marginBottom: 5 }}>
                Formulas
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {formulaKeys.map((fk) => (
                  <div key={fk} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                    <span style={{ fontSize: 10, color: "#888", minWidth: 72, flexShrink: 0, textAlign: "right" }}>
                      {formatKey(fk).replace("formula ", "")}
                    </span>
                    <code style={{
                      fontSize: 11,
                      fontFamily: "monospace",
                      color: "#1a4a1a",
                      background: "#eaf4ea",
                      border: "1px solid #c3e0c3",
                      borderRadius: 3,
                      padding: "2px 6px",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}>
                      {String(expl[fk])}
                    </code>
                  </div>
                ))}
              </div>
            </div>
          )}
          {breakdownKeys.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#666", marginBottom: 5 }}>
                Breakdown
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px" }}>
                {breakdownKeys.map((bk) => {
                  const v = expl[bk];
                  const isFlag = typeof v === "boolean";
                  return (
                    <span key={bk} style={{ fontSize: 11, color: "#444", whiteSpace: "nowrap" }}>
                      <span style={{ color: "#888" }}>{formatKey(bk)}: </span>
                      <span style={{ fontWeight: 600, color: isFlag ? (v ? "#1a7a1a" : "#999") : "#222" }}>
                        {formatValue(v)}
                      </span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

interface InvoicePreviewProps {
  payload: unknown;
  variant: "success" | "error";
}

function InvoicePreview({ payload, variant }: InvoicePreviewProps) {
  const isError = variant === "error";
  const containerStyle: React.CSSProperties = {
    marginTop: 12,
    padding: 16,
    maxHeight: "min(70vh, 700px)",
    overflow: "auto",
    background: isError ? "#fff3f3" : "#fafafa",
    border: isError ? "1px solid #e0b4b4" : "1px solid #ddd",
    borderRadius: 4,
    fontSize: 13,
  };

  if (typeof payload === "string") {
    return (
      <div data-testid="completed-posting-invoice-preview-json" style={containerStyle}>
        <span style={{ color: isError ? "#c0392b" : "#333" }}>{payload}</span>
      </div>
    );
  }

  if (typeof payload !== "object" || payload === null) {
    return (
      <div data-testid="completed-posting-invoice-preview-json" style={containerStyle}>
        <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {JSON.stringify(payload, null, 2)}
        </pre>
      </div>
    );
  }

  const record = payload as Record<string, unknown>;
  const scalarEntries: [string, unknown][] = [];
  const arrayEntries: [string, unknown[]][] = [];
  const objectEntries: [string, Record<string, unknown>][] = [];

  for (const [key, val] of Object.entries(record)) {
    if (Array.isArray(val)) {
      arrayEntries.push([key, val]);
    } else if (val !== null && typeof val === "object") {
      objectEntries.push([key, val as Record<string, unknown>]);
    } else {
      scalarEntries.push([key, val]);
    }
  }

  const cellStyle: React.CSSProperties = {
    padding: "6px 10px",
    borderBottom: "1px solid #e8e8e8",
    verticalAlign: "top",
    whiteSpace: "nowrap",
  };
  const headerCellStyle: React.CSSProperties = {
    ...cellStyle,
    background: "#f0f0f0",
    fontWeight: 600,
    textTransform: "capitalize",
  };
  const labelStyle: React.CSSProperties = {
    color: "#666",
    fontWeight: 500,
    minWidth: 160,
    display: "inline-block",
  };

  return (
    <div data-testid="completed-posting-invoice-preview-json" style={containerStyle}>
      {scalarEntries.length > 0 && (
        <div style={{ marginBottom: arrayEntries.length > 0 ? 16 : 0 }}>
          {scalarEntries.map(([k, v]) => (
            <div key={k} style={{ marginBottom: 4 }}>
              <span style={labelStyle}>{formatKey(k)}:</span>
              <span style={{ color: "#222" }}>{formatValue(v)}</span>
            </div>
          ))}
        </div>
      )}

      {objectEntries.map(([key, obj]) => (
        <div key={key} style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, textTransform: "capitalize", color: "#444" }}>
            {formatKey(key)}
          </div>
          {Object.entries(obj).map(([k, v]) => (
            <div key={k} style={{ marginBottom: 4, paddingLeft: 12 }}>
              <span style={labelStyle}>{formatKey(k)}:</span>
              <span style={{ color: "#222" }}>{formatValue(v)}</span>
            </div>
          ))}
        </div>
      ))}

      {arrayEntries.map(([key, rows]) => {
        if (rows.length === 0) return null;
        const allKeys = Array.from(
          new Set(rows.flatMap((r) => (r && typeof r === "object" ? Object.keys(r as object) : [])))
        );
        if (allKeys.length === 0) {
          return (
            <div key={key} style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 6, textTransform: "capitalize", color: "#444" }}>
                {formatKey(key)}
              </div>
              <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>
                {JSON.stringify(rows, null, 2)}
              </pre>
            </div>
          );
        }

        const hasGroupingKey = allKeys.includes("groupingKey");
        const hasCalcExplanation = allKeys.includes("calcExplanation");
        const colKeys = allKeys.filter((k) => k !== "groupingKey" && k !== "calcExplanation");

        const groupedRows: { groupKey: string | null; items: { row: unknown; originalIndex: number }[] }[] = [];
        if (hasGroupingKey) {
          for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const gk = row && typeof row === "object" ? String((row as Record<string, unknown>)["groupingKey"] ?? "") : "";
            const existing = groupedRows.find((g) => g.groupKey === gk);
            if (existing) {
              existing.items.push({ row, originalIndex: i });
            } else {
              groupedRows.push({ groupKey: gk, items: [{ row, originalIndex: i }] });
            }
          }
        }

        let stripeIndex = 0;

        const renderDataRow = (row: unknown, originalIndex: number, bg: string) => {
          const expl = hasCalcExplanation && row && typeof row === "object"
            ? (row as Record<string, unknown>)["calcExplanation"] as Record<string, unknown> | undefined
            : undefined;
          return (
            <React.Fragment key={originalIndex}>
              <tr style={{ background: bg }}>
                {colKeys.map((k) => {
                  const v = row && typeof row === "object" ? (row as Record<string, unknown>)[k] : undefined;
                  return (
                    <td key={k} style={{ ...cellStyle, whiteSpace: typeof v === "string" && v.length > 40 ? "normal" : "nowrap" }}>
                      {formatValue(v)}
                    </td>
                  );
                })}
              </tr>
              {expl != null && <CalcExplanation expl={expl} colCount={colKeys.length} />}
            </React.Fragment>
          );
        };

        return (
          <div key={key} style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, textTransform: "capitalize", color: "#444" }}>
              {formatKey(key)} ({rows.length})
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
                <thead>
                  <tr>
                    {colKeys.map((k) => (
                      <th key={k} style={headerCellStyle}>{formatKey(k)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {hasGroupingKey
                    ? groupedRows.map(({ groupKey, items }) => (
                        <React.Fragment key={groupKey ?? "__no_group__"}>
                          <tr>
                            <td
                              colSpan={colKeys.length}
                              style={{
                                padding: "5px 10px",
                                background: "#e8edf5",
                                fontWeight: 600,
                                color: "#2c4a7c",
                                borderTop: "2px solid #c5d0e6",
                                borderBottom: "1px solid #c5d0e6",
                                fontSize: 11,
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                              }}
                            >
                              {groupKey || "—"}
                            </td>
                          </tr>
                          {items.map(({ row, originalIndex }) =>
                            renderDataRow(row, originalIndex, stripeIndex++ % 2 === 0 ? "#fff" : "#f7f7f7")
                          )}
                        </React.Fragment>
                      ))
                    : rows.map((row, i) =>
                        renderDataRow(row, i, i % 2 === 0 ? "#fff" : "#f7f7f7")
                      )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function InvoicePreviewButton({ show, loading, payload, variant, onDownload }: Props) {
  return (
    <div data-testid="completed-posting-invoice-preview-button-wrapper">
      <Button
        data-testid="completed-posting-invoice-preview-button"
        style={{ display: show ? "inline-flex" : "none" }}
        className="button-gradient margin-bottom-16"
        disabled={loading}
        onClick={onDownload}
      >
        Load invoice preview
        {loading && <CircularProgress size={20} className="buttonLoader" />}
      </Button>
      {payload != null && variant != null && (
        <InvoicePreview payload={payload} variant={variant} />
      )}
    </div>
  );
}
